import { createApp, defineAsyncComponent } from 'vue';
import axios from 'axios';
import LoadScript from 'vue-plugin-load-script';
import { createPinia, mapWritableState } from 'pinia';
import VueLazyLoad from 'vue3-lazyload';
import EqualHeightsPlugin from '../../Plugins/EqualHeights/EqualHeightsPlugin';
import { VideoPopupSession } from '../../../../js/services/VideoPopupService';
import { useRegistrationStore } from '../../store/Registration/RegistrationStore';
import {
  fireCTAClickEvents,
  fireGetCouponClickEvent,
  fireTagPrintEvent,
  onCommonLinkLogs,
} from '../../Events/TagManagerServices/TaggingEventForPages';
import { useBuyNowModalStore } from '@vue3/src/store/Shared/BuyNowModalStore/BuyNowModalStore';
import { Events } from '@vue3/src/Events/ConsumerEvents/Events';
import { CheckForQuickStartPopUp } from '@/js/services/CheckForQuickStartPopUp';
import { BonusOfferPopupSession } from '@/js/services/BonusOfferPopupSessionService';
import { SERVICE_SPECIAL_OFFER_BANNER_CAROUSEL_ASSET_NAME } from '@/js/services/ShiftDigital/ShiftDigitalConstants';
import ShiftDigitalService from '@vue3/src/Events/ShiftDigital/ShiftDigitalService';
import { useLeadFormsStore } from '@vue3/src/store/LeadForms/LeadFormsStore';
import { useFinancingPageDetailsStore } from '../../store/FinancingPageDetailsStore/FinancingPageDetailsStore';
import { VSlickCarousel } from 'v-slick-carousel';
import ShiftDigitalContainer from '../../Events/ShiftDigital/ShiftDigitalContainer';
import { toggleModal } from '../../Composables/LeadFormSettings/useLeadFormSettings';
import { CtaButtonActionService } from '../../Services/CtaButtonActionService';
import { ScheduleTestDrivePopupSession } from '../../../../js/services/ScheduleTestDrivePopupSessionService';
import { TagManagerContainer } from '../../Events/GoogleTagManager/TagManagerContainer';
import { fireTrackingPageViews } from '../../Events/TrackerServices/TrackerServices';
import { LANDING_PAGE_VIEW } from '../../../../js/services/SonicDataLayer/SonicDataConstants';
import {
  isSatelliteLoaded,
  triggerInventoryScrollEventOnHomePage,
  triggerSpecialsScrollEventOnHomePage,
} from '../../Events/FordDirect/FordCookieService';
import { useTextModalStore } from '../../store/Shared/TextModalStore/TextModalStore';
import { LEAD_FORM_REWRITES } from '../../../../js/constants/constants';
import { fireSonicScheduleServiceClick } from '../../Events/SonicDataLayer/SonicDataService';

const landingPage = {
  components: {
    MainHeader: defineAsyncComponent(() => import('../../Components/MainHeader/MainHeader')),
    MainFooter: defineAsyncComponent(() => import('../../Components/MainFooter/MainFooter')),
    PreFooterContainer: defineAsyncComponent(() =>
      import('@vue3/src/Components/PreFooterContainer/PreFooterContainer.vue')
    ),
    CommonComponents: defineAsyncComponent(() =>
      import('@vue3/src/Components/CommonComponents/CommonComponents.vue')
    ),
    VideoTag: defineAsyncComponent(() => import('@vue3/src/Components/VideoTag/VideoTag.vue')),
    LeadFormLink: defineAsyncComponent(() =>
      import('@vue3/src/Components/LeadFormLink/LeadFormLink/LeadFormLink.vue')
    ),

    LazyImageCustom: defineAsyncComponent(() =>
      import('../../Components/LazyImageCustom/LazyImageCustom.vue')
    ),

    disasterPopup: defineAsyncComponent(() =>
      import('../../../../js/components/disasterPopUp/disasterPopUp.vue')
    ),

    RotationalBanner: defineAsyncComponent(() =>
      import('../../Components/RotationalBanners/RotationalBanners.vue')
    ),

    videopopupModal: defineAsyncComponent(() =>
      import('../../Components/PopupVideoModal/PopupVideoModal.vue')
    ),

    CountDownClock: defineAsyncComponent(() =>
      import('../../../../js/components/shared/TradeAssistance/CountDownClock/CountDownClock.vue')
    ),

    tileImages: defineAsyncComponent(() => import('../../Components/TileImages/TileImages.vue')),

    BrandBrowseDetails: defineAsyncComponent(() =>
      import('@/js/components/BrandBrowseDetails/BrandBrowseDetails.vue')
    ),

    MitsubishiPopUp: defineAsyncComponent(() =>
      import('../../Components/MitsubishiPopup/MitsubishiPopup.vue')
    ),

    ExploreResearchModels: defineAsyncComponent(() =>
      import('../../Components/ExploreResearchModels/ExploreResearchModels.vue')
    ),

    CamRotationalBanner: defineAsyncComponent(() =>
      import('../../Components/CamRotationalBanner/CamRotationalBanner.vue')
    ),

    QuickStartOverlay: defineAsyncComponent(() =>
      import('@vue3/src/Components/QuickStartOverlay/QuickStartOverlay.vue')
    ),

    BrowseByType: defineAsyncComponent(() =>
      import('@/js/components/BrowseByType/BrowseByType.vue')
    ),

    BrowseByBrand: defineAsyncComponent(() =>
      import('@/js/components/BrowseByBrand/BrowseByBrand.vue')
    ),

    HorizontalDealerHours: defineAsyncComponent(() =>
      import('../../Components/DealerHours/DealerHours/HorizontalDealerHours.vue')
    ),
    FlipCountDownTimer: defineAsyncComponent(() =>
      import('../../Components/FlipCountDownTimer/FlipCountDownTimer.vue')
    ),
    VehicleSearch: defineAsyncComponent(() =>
      import('../../Components/NewVehicleSearch/VehicleSearch/VehicleSearch.vue')
    ),
    NewVehicleSearch: defineAsyncComponent(() =>
      import(
        '../../Components/NewVehicleSearch/NewVehicleSearchContainer/NewVehicleSearchContainer.vue'
      )
    ),
    CommonVehicleSearch: defineAsyncComponent(() =>
      import('../../Components/InventoryVehicleSearch/InventoryVehicleSearch.vue')
    ),
    VehicleSearchWhiteTheme: defineAsyncComponent(() =>
      import('../../Components/NewVehicleSearch/MazdaOemVehicleSearch.vue')
    ),
    SpecialBanners: defineAsyncComponent(() =>
      import('../../Components/SpecialBanners/SpecialBanners.vue')
    ),
    PrimaryRotationalBannerContainer: defineAsyncComponent(() =>
      import(
        '../../Components/RotaionalSpecialBanner/RotationalSpecialsContainer/PrimaryRotationalBannerContainer.vue'
      )
    ),
    DealerMapDirections: defineAsyncComponent(() =>
      import('../../Components/MapDirections/PrimaryMapDirection/PrimaryMapDirection.vue')
    ),
    DealerMap: defineAsyncComponent(() =>
      import('../../Components/MapDirections/MapView/MapView.vue')
    ),
    BmwNewVehicleCount: defineAsyncComponent(() =>
      import('@/js/components/shared/BmwNewVehicleCount/BmwNewVehicleCount.vue')
    ),
    PrimaryVehicleCount: defineAsyncComponent(() =>
      import('../../Components/VehicleCount/VehicleCountContainer/VehicleCountContainer.vue')
    ),
    usedVehicleSpecials: defineAsyncComponent(() =>
      import(
        '../../Components/RotaionalSpecialBanner/RotationalUsedSpecialBanner/rotationalusedspecialbanners.vue'
      )
    ),
    preownedRotationalSpecials: defineAsyncComponent(() =>
      import('../../Components/PreownedRotationalSpecials/PreownedRotationalSpecials.vue')
    ),
    AstonMartinModels: defineAsyncComponent(() =>
      import('../../Components/AstonmartinModels/AstonmartinModels.vue')
    ),
    RotationalGoogleReview: defineAsyncComponent(() =>
      import('../../Components/Review/RotationalGoogleReview.vue')
    ),
    LeadFormLink: defineAsyncComponent(() =>
      import('../../Components/LeadFormLink/LeadFormLink/LeadFormLink.vue')
    ),
    // RotationalBlogs: defineAsyncComponent(() => {
    //   import ('../../Components/Blogs/RotationalBlogs/RotationalBlogs.vue')
    // })
  },
  data() {
    return {
      setMatchHeightData: {
        el: ['.section-one', '.section-two', '.separator-line', '.couponspecials'],
      },
      currentIndex: 0,
      isRotationalCouponsNewDesign: window.isRotationalCouponsNewDesign,
      currentDate: new Date(),
      showMitsubhishiPopUp: window.campaignId === '3309',
      showLeadForm: false,
      showLandingPopUpModal: false,
      showVideoPopupModalMobile: false,
      shiftDigital: '',
      titleText: 'Disclosure',
      eventids: Events.eventsConstants,
      ScheduleServiceForm: false,
      serviceHistoryBoxes: [],
      showdisclaimermodal: false,
      isprimarylanding: true,
      showEmailOffer: false,
      showiframelogin: false,
      disclaimertext: '',
      serviceHistoryForm: false,
      openGetOffer: false,
      servicehistorydata: '',
      openBuyNowPopUp: false,
      buyNowPopUpType: 'new',
      mapdetails: '',
      showslick: false,
      userScroll25PercentRegistered: false,
      inventoryVehicledetails: '',
      showLandingScheduleTestDrivePopUp: false,
      newSlickCoupons: {
        groupsToShow: 1,
        groupsToScroll: 1,
        arrows: false,
        rows: 0,
        dots: false,
        nextArrow: `<em class="primaryicon-right-arrow-light d-none d-md-block ft-24 position-absolute" style="cursor:pointer;z-index:1;right:-20px;top:45%"></em>`,
        prevArrow: `<em class="primaryicon-left-arrow-light-format d-none d-md-block ft-24 position-absolute" style="cursor:pointer;z-index:1;left:-20px;top:45%"></em>`,
        responsive: [
          {
            breakpoint: 1700,
            settings: {
              groupsToShow: 4,
              groupsToScroll: 4,
              infinite: true,
              arrows: true,
              dots: false,
            },
          },
          {
            breakpoint: 1366,
            settings: {
              groupsToShow: 4,
              groupsToScroll: 4,
              infinite: true,
              arrows: true,
              dots: false,
            },
          },
          {
            breakpoint: 1024,
            settings: {
              groupsToShow: 3,
              groupsToScroll: 3,
              infinite: true,
              arrows: true,
              dots: true,
            },
          },
          {
            breakpoint: 991,
            settings: {
              arrows: true,
              groupsToShow: 2,
              groupsToScroll: 2,
            },
          },
          {
            breakpoint: 767,
            settings: {
              dots: false,
              groupsToShow: 1,
              groupsToScroll: 1,
            },
          },
          {
            breakpoint: 600,
            settings: {
              groupsToShow: 1,
              groupsToScroll: 1,
            },
          },
          {
            breakpoint: 480,
            settings: {
              groupsToShow: 1,
              groupsToScroll: 1,
            },
          },
        ],
      },
      couponsslickoptions: {
        infinite: true,
        groupsToShow: 1,
        groupsToScroll: 1,
        arrows: false,
        dots: false,
        rows: 0,
        nextArrow: `<em class="primaryicon-right-arrow bgcolor-primary _msbi_background_color" style="cursor:pointer;z-index:1"></em>`,
        prevArrow: `<em class="primaryicon-left-arrow" style="color:#cccccc;cursor:pointer;z-index:1"></em>`,
        responsive: [
          {
            breakpoint: 1700,
            settings: {
              groupsToShow: 4,
              groupsToScroll: 4,
              infinite: true,
              arrows: true,
              dots: false,
            },
          },
          {
            breakpoint: 1366,
            settings: {
              groupsToShow: 4,
              groupsToScroll: 4,
              infinite: true,
              arrows: true,
              dots: false,
            },
          },
          {
            breakpoint: 1024,
            settings: {
              groupsToShow: 3,
              groupsToScroll: 3,
              infinite: true,
              arrows: true,
              dots: false,
            },
          },
          {
            breakpoint: 991,
            settings: {
              arrows: true,
              groupsToShow: 2,
              groupsToScroll: 2,
            },
          },
          {
            breakpoint: 600,
            settings: {
              groupsToShow: 2,
              groupsToScroll: 2,
            },
          },
          {
            breakpoint: 480,
            settings: {
              groupsToShow: 1,
              groupsToScroll: 1,
            },
          },
        ],
      },
      slickOptions: {
        infinite: true,
        groupsToShow: 3,
        groupsToScroll: 3,
        dots: true,
        arrows: false,
        responsive: [
          {
            breakpoint: 1366,
            settings: {
              groupsToShow: 4,
              groupsToScroll: 3,
              infinite: true,
              dots: true,
            },
          },
          {
            breakpoint: 1024,
            settings: {
              groupsToShow: 4,
              groupsToScroll: 3,
              infinite: true,
              arrows: true,
              dots: true,
            },
          },
          {
            breakpoint: 991,
            settings: {
              arrows: true,
              groupsToShow: 2,
              groupsToScroll: 2,
            },
          },
          {
            breakpoint: 600,
            settings: {
              groupsToShow: 2,
              groupsToScroll: 2,
            },
          },
          {
            breakpoint: 480,
            settings: {
              groupsToShow: 1,
              groupsToScroll: 1,
            },
          },
        ],
        nextArrow: `<em class="primaryicon-right-arrow bgcolor-primary _msbi_background_color" style="cursor:pointer;z-index:1"></em>`,
        prevArrow: `<em class="primaryicon-left-arrow" style="color:#cccccc;cursor:pointer;z-index:1"></em>`,
      },
      showcontactform: false,
      RecallAlertDetails: [],
      showSignInModal: false,
      showFullServiceHistory: false,
      loadMobileCompoments: false,
      loadimagecomponent: false,
      loadMapCompoments: false,
      serviceHistoryViewDetails: '',
      showVideoPopupModal: false,
      searchButtonLeadFormComponent: '',
      showSearchButtonLeadForm: false,
      buyNowPopType: 'new',
      showBuyNowPopUp: false,
      bonusOfferShowAtEntryData: '',
      showOtherPopUpModals: false,
      isHideDirections: window.hideDirectionsButton,
      selectedLocationByUser: '',
      eventDrivenSessionCount: sessionStorage.getItem('eventcount')
        ? parseInt(sessionStorage.getItem('eventcount'))
        : 0,
      bonusOfferEventDrivenCount: window.EventDrivenBonusOfferSalesCount
        ? parseInt(window.EventDrivenBonusOfferSalesCount)
        : 0,
    };
  },
  created() {
    if (window.innerWidth > 800) {
      this.loadimagecomponent = true;
      this.loadMobileCompoments = true;
    }
    const self = this;
    let throttleTimeout = null;

    document.addEventListener(
      'DOMContentLoaded',
      function () {
        window.addEventListener(
          'scroll',
          function () {
            if (!throttleTimeout) {
              throttleTimeout = setTimeout(() => {
                self.handleScroll();
                throttleTimeout = null;
              }, 100);
            }
          },
          { passive: true }
        );
      },
      false
    );
  },

  watch: {
    bonusOfferShowAtEntryData() {
      if (!this.showMitsubhishiPopUp) {
        if (!(window.enablePopupVideo && window.videoURL)) {
          this.showBonusOfferModalOnEntry();
        }
      }
    },
    showVideoPopupModal() {
      if (!this.showVideoPopupModal || !this.showVideoPopupModalMobile) {
        this.showBonusOfferModalOnEntry();
      }
    },
    showMitsubhishiPopUp() {
      if (!this.showMitsubhishiPopUp) {
        this.showVideoPopup();
      }
    },
    eventDrivenSessionCountState() {
      this.eventDrivenSessionCount = sessionStorage.getItem('eventcount')
        ? parseInt(sessionStorage.getItem('eventcount'))
        : 0;
      this.showBonusModalEventDriven();
    },
  },

  computed: {
    ...mapWritableState(useBuyNowModalStore, ['buyNowPopType', 'openBuyNowPopUp']),
    ...mapWritableState(useRegistrationStore, ['showSignInModal', 'showLocationModal']),
    ...mapWritableState(useLeadFormsStore, ['showBonusOfferModal']),
    ...mapWritableState(useTextModalStore, ['showTextModal', 'title', 'text']),
    ...mapWritableState(useFinancingPageDetailsStore, {
      eventDrivenSessionCountState: 'eventDrivenSessionCount',
    }),
  },

  methods: {
    next() {
      this.$refs.coupons.next();
      this.currentIndex = this.$refs.coupons.currentSlideGroupIndex;
    },
    prev() {
      this.$refs.coupons.prev();
      this.currentIndex = this.$refs.coupons.currentSlideGroupIndex;
    },
    linkClickHandler(event) {
      if (!event.currentTarget) return;
      if (event.currentTarget.href?.includes('#modal')) {
        onModalLinkClick(event.currentTarget.href, event.currentTarget.target, event);
      } else {
        window.open(event.currentTarget.href || '/', event.currentTarget.target || '_self');
      }
    },
    onCloseBonusOffer() {
      this.showBonusOfferModal = false;
    },
    showBonusModalEventDriven() {
      let bonusOfferSession = BonusOfferPopupSession.bonusOfferSessionExists();
      let isEventDrivenPopupEnabled = window.isEventDrivenPopupEnabled === 'True';
      if (isEventDrivenPopupEnabled) {
        if (this.bonusOfferEventDrivenCount === 0) {
          setTimeout(() => {
            if (!bonusOfferSession && !this.showExpressCheckoutModal) {
              if (window.ismobile && window.disableBonusOfferOnMobile) {
                this.showBonusOfferModal = false;
              } else {
                this.showBonusOfferModal = true;
                BonusOfferPopupSession.setBonusOfferEventDrivenSession();
              }
            }
          }, 10000);
        } else if (
          !bonusOfferSession &&
          !this.showExpressCheckoutModal &&
          this.bonusOfferEventDrivenCount <= parseInt(this.eventDrivenSessionCount)
        ) {
          if (window.ismobile && window.disableBonusOfferOnMobile) {
            this.showBonusOfferModal = false;
          } else {
            this.showBonusOfferModal = true;
            BonusOfferPopupSession.setBonusOfferEventDrivenSession();
          }
        }
      }
    },

    showBonusOfferModalOnEntry() {
      if (this.bonusOfferShowAtEntryData?.showAtEntry) {
        let bonusOfferSession = BonusOfferPopupSession.bonusOfferSessionExists();
        if (!bonusOfferSession) {
          this.showBonusOfferModal = true;
          BonusOfferPopupSession.setBonusOfferEventDrivenSession();
        }
      }
    },

    showVideoPopup() {
      if (window.enablePopupVideo && window.videoURL && !window.ismobile) {
        let videoPopupSessionKey = VideoPopupSession.VideoPopupSessionExists('landingpage');
        if (!videoPopupSessionKey) {
          this.showVideoPopupModal = true;
        }
      } else if (window.enablePopupVideoMobile && window.videoURLMobile && window.ismobile) {
        let videoPopupSessionKey = VideoPopupSession.VideoPopupSessionExists('landingpage');
        if (!videoPopupSessionKey) {
          this.showVideoPopupModalMobile = true;
        }
      } else {
        this.showBonusOfferModalOnEntry();
      }
    },

    onOpenBuyParagonHonda(type = 'new') {
      this.buyNowPopUpType = type;

      this.openBuyNowPopUp = true;
    },

    onShowSignInModal() {
      this.showSignInModal = true;

      fireCTAClickEvents('Sign In', { label: 'Sign In', url: '/account/login' });
    },

    getDocHeight() {
      let D = document;

      return Math.max(
        D.body.scrollHeight,
        D.documentElement.scrollHeight,
        D.body.offsetHeight,
        D.documentElement.offsetHeight,
        D.body.clientHeight,
        D.documentElement.clientHeight
      );
    },

    handleScroll() {
      let winheight =
        window.innerHeight || (document.documentElement || document.body).clientHeight;

      let docheight = this.getDocHeight();

      let scrollTop =
        window.pageYOffset ||
        (document.documentElement || document.body.parentNode || document.body).scrollTop;

      let trackLength = docheight - winheight;

      let pctScrolled = Math.floor((scrollTop / trackLength) * 100);

      if (pctScrolled >= 15 && !this.loadMobileCompoments) {
        this.loadimagecomponent = true;

        this.loadMobileCompoments = true;
      }

      let footerHeight = 0;

      try {
        if (window.innerWidth < 800) {
          footerHeight = document.querySelector('#paragon-websiteprimary-footer').offsetHeight;
        } else {
          footerHeight = document.querySelector('#website-primary-footer').offsetHeight;
        }
      } catch (e) {
        console.error('Error getting footer height:', e);
      }

      if (scrollTop + winheight >= docheight - footerHeight) {
        this.loadMapCompoments = true;
      }

      if (!this.userScroll25PercentRegistered && pctScrolled >= 25) {
        logGoogleBingEvent('scroll tracking', 'scroll', '25%', 1, 'scroll tracking page');

        this.checkDatabaseEvent();

        this.userScroll25PercentRegistered = true;
      }
    },

    checkDatabaseEvent() {
      let sessionValue = window.tid;

      let localSessionValue = localStorage.getItem('databaseevent');

      if (localSessionValue) {
        if (localSessionValue !== sessionValue) {
          this.fireEvent(Events.eventsConstants.DatabaseScrollEvent);

          localStorage.setItem('databaseevent', sessionValue);
        }
      } else {
        this.fireEvent(Events.eventsConstants.DatabaseScrollEvent);

        localStorage.setItem('databaseevent', sessionValue);
      }
    },

    onVehicleBtnLeave(btnColor, event, fontColor) {
      try {
        if (event.target.classList.contains('rounded-full-buttons')) {
          if (btnColor) {
            event.target.style.setProperty('background-color', btnColor, 'important');
          } else {
            event.target.style.removeProperty('background-color');
          }
          if (fontColor) {
            event.target.style.setProperty('color', fontColor, 'important');
          } else {
            event.target.style.removeProperty('color');
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    onVehicleBtnHover(btnColor, event, hoverTextColor) {
      try {
        if (btnColor || hoverTextColor) {
          if (event.target.classList.contains('rounded-full-buttons')) {
            btnColor && event.target.style.setProperty('background-color', btnColor, 'important');
            hoverTextColor && event.target.style.setProperty('color', hoverTextColor, 'important');
          }
        }
      } catch (error) {
        console.log(error);
      }
    },

    onImageClick(Category, Action, Label, id, labelText, url) {
      this.logTagClickEvents('Banner', url);
      logGoogleBingEvent(Category, Action, Label, id, labelText);
    },

    logTagClickEvents(label, url, event) {
      if (
        url &&
        (url.toLowerCase() === 'schedule-service' || url.toLowerCase() === 'getfinancing')
      ) {
        if (event?.preventDefault) {
          event.preventDefault();
          CtaButtonActionService.getCtaButtonAction(url.toLowerCase());
        }
      }
      setTimeout(() => {
        let tagManager = TagManagerContainer.getDealerObject();
        if (tagManager && typeof tagManager.getLinkClickEvent === 'function') {
          tagManager.getLinkClickEvent(label, url.toLowerCase());
        }
      }, 0);
    },

    onLeadFormOpen(name) {
      if (name.toLowerCase() === 'scheduleservice') {
        fireSonicScheduleServiceClick(name);
        this.logTagClickEvents('ScheduleService', '/scheduleservice');
      }

      if (LEAD_FORM_REWRITES.some((t) => t.toLowerCase() === name.toLowerCase())) {
        CtaButtonActionService.getCtaButtonAction(name.toLowerCase());
        return;
      }

      this.searchButtonLeadFormComponent = name;
      this.showSearchButtonLeadForm = true;

      this.commonLogs(name);
    },

    showFlipDisclaimer(text) {
      this.titleText = 'Disclosure';
      this.showdisclaimermodal = true;
      this.disclaimertext = text;
    },

    handleAfterChange(event, slick, currentSlide) {
      if (this.shiftDigital) {
        let carouselClick = {
          assetPosition: currentSlide,
          assetName: SERVICE_SPECIAL_OFFER_BANNER_CAROUSEL_ASSET_NAME,
        };
        ShiftDigitalService.shiftDigitalCarouselClick(this.shiftDigital, carouselClick);
      }
    },

    onServiceViewHistoryDetailsClick(value) {
      if (this.serviceHistoryBoxes.indexOf(value) !== -1) {
        let servicehistoryboxes = this.serviceHistoryBoxes.filter((item) => {
          return item !== value;
        });

        this.serviceHistoryBoxes = servicehistoryboxes;
      } else {
        this.serviceHistoryBoxes.push(value);
      }
      onCommonLinkLogs({ label: 'View Details' });
    },

    isServiceHistoryPresent(value) {
      return this.serviceHistoryBoxes.some((item) => {
        return item === value;
      });
    },

    openScheduleServiceForm(offerid = '', styleid = '', vin = '', dealerurl = '') {
      this.logTagClickEvents('ScheduleService', '/scheduleservice');
      this.offerid = offerid;
      this.styleid = styleid;
      this.vin = vin;
      this.dealerurl = dealerurl;
      let eventmodel = {
        ...Events.consumerEventModel,
        consumerEventId: Events.eventsConstants.openedScheduleService,
        offerId: this.offerid,
        vin: this.vin,
        styleId: this.styleid,
      };

      Events.logConsumerEvent(eventmodel);

      logGoogleBingEvent(
        'scheduleserviceform',
        'scheduleservice_click',
        'scheduleservice',
        1,
        'Schedule Service form Click'
      );
      CtaButtonActionService.getCtaButtonAction('schedule-service');
      onCommonLinkLogs({ label: 'schedule', url: '/scheduleservice' });
    },

    onScheduleServiceForm() {
      let eventModel = {
        ...Events.consumerEventModel,
      };
      eventModel.consumerEventId = Events.eventsConstants.ScheduleServiceClicked;
      Events.logConsumerEvent(eventModel);
      CtaButtonActionService.getCtaButtonAction('schedule-service');
    },

    fireEvent(eventid) {
      let eventModel = {
        ...Events.consumerEventModel,
        consumerEventId: eventid,
      };
      Events.logConsumerEvent(eventModel);
    },

    onScheduleServiceOpen() {
      if (this.shiftDigital) {
        ShiftDigitalService.scheduleServiceClick(this.shiftDigital);
      }
      this.onScheduleServiceForm();
    },

    // onDisclaimerClick(data) {
    //   debugger;
    //   // this.disclaimertext = data;
    //   // this.showdisclaimermodal = true;
    //  this.text = data;
    //  this.title='Disclosure'
    //   this.showTextModal=true
    // },
    onDisclaimerClick(data, empty = '', offerDetails = { offerHeading: '' }) {
      this.text = data;
      this.title = 'Disclosure';
      this.showTextModal = true;
      try {
        fireGetCouponClickEvent({
          offerHeading: offerDetails.offerHeading,
          engagedLink: 'View Details',
        });
      } catch (e) {
        console.error(e);
      }
    },
    async onReadMoreDescription(id) {
      let response = await axios.get(`${window.ApiBaseUrl}/Specials/GetOfferById?offerId=${id}`);
      if (response?.data?.description) {
        this.text = response.data.description;
        this.title = 'Description';
        this.showTextModal = true;
      }
    },
    onmapstructuregot(map) {
      if (!window.isDealerGroup && !window.secondaryAddress) {
        this.mapdetails = '';
      }
      setTimeout(() => {
        this.mapdetails = map;
      }, 100);
    },

    onContactusClick() {
      this.showcontactform = true;
    },

    onShowBonusModal() {
      this.showBonusOfferModal = true;
    },

    closeActivation() {
      document.querySelector('.activation-alertmessage').remove();
    },

    onGetOfferClick(offerid, styleid, vin, dealerurl, offerdesignid) {
      toggleModal('showGetOfferForm', true, {
        styleid: styleid,
        offerid: offerid,
        vin: vin,
        offerdesignid: offerdesignid,
        dealerurl: dealerurl,
        consumereventid: Events?.eventsConstants?.GotServiceCoupon,
      });
      logGoogleBingEvent(
        'Coupon Specials Page',
        'Button_Click',
        'Get Coupon',
        1,
        'Get Coupon Click'
      );

      // this.openGetOffer = true;
      this.offerdesignid = offerdesignid;
      this.offerid = offerid;
      this.styleid = styleid;
      this.vin = vin;
      this.dealerurl = dealerurl;
      this.consumereventid = Events.eventsConstants.GotServiceCoupon;
    },

    onPrintClick(offerid, styleid, offerType) {
      Events.specialsEvent(
        Events.eventsConstants.Printedserviceoffer,
        styleid,
        '',
        offerid,
        offerType
      );
      logGoogleBingEvent('Service', 'Print', 'Service_offer', 1, 'Print Button Click - Service');
      this.printTagEvents(offerid);
      fireTagPrintEvent('service');
    },

    printTagEvents(offerid) {
      let tagManager = TagManagerContainer.getDealerObject();

      if (tagManager && typeof tagManager.getPrintEvent === 'function') {
        tagManager.getPrintEvent(`${offerid}`);
      }
    },

    openParagonPopup(type) {
      this.buyNowPopType = 'used';
      if (type === 'paragongroupinventorynewpopup') {
        this.buyNowPopType = 'new';
      }
      this.showBuyNowPopUp = true;
    },

    getBonusOfferShowAtEntry() {
      axios
        .get(`${window.ApiBaseUrl}/Leads/bonusoffer/${window.campaignId}/0/0`)
        .then((response) => {
          this.bonusOfferShowAtEntryData = response.data;
        });
    },

    closeLandingPopUpModal() {
      this.showLandingPopUpModal = false;
    },

    openMapsInMobile() {
      if (this.shiftDigital) {
        this.shiftDigital.submitEvent(SHIFT_DIGITAL_GET_DIRECTIONS_EVENT);
      }

      let selectedLocationByUser = MapService.openMapsAppInMobile(window.fullAddress);

      if (this.isMobile) {
        window.location.href = selectedLocationByUser;
      } else {
        window.location.href = '/get-directions';
      }
    },

    onLoadCalls() {
      try {
        if (!document.getElementById('vehicle-search-section') || window.searchBarPositionPrimary) {
          if (document.getElementsByClassName('move-margin-top')[0]) {
            document.getElementsByClassName('move-margin-top')[0].style.top = '0px';
          }
        }
      } catch (e) {
        console.log(e);
      }
    },

    checkForSlickEvents() {
      // Method referenced in Vue2 mounted() - likely handles slick carousel event bindings
      // Implementation may need to be added based on specific requirements
    },

    // couponSpecials

    onAppleWalletClick(offerid, styleid, heading) {
      Events.specialsEvent(Events.eventsConstants.AppleWallet, styleid, '', offerid);
      logGoogleBingEvent(
        'apollo_service_conv',
        'download',
        'apple_wallet_' + heading,
        1,
        'apple wallet download page'
      );
    },

    // ...existing code...
  },

  async mounted() {
    let scheduleTestDriveSession =
      ScheduleTestDrivePopupSession.ScheduleTestDriveSessionExists('-ladingTestDrivePopup');
    if (
      !scheduleTestDriveSession &&
      window.make.toLowerCase() === 'maserati' &&
      window.isMaseratiGrecaleHomePagePopUp
    ) {
      this.showLandingScheduleTestDrivePopUp = true;
      ScheduleTestDrivePopupSession.setScheduleTestDriveSession('-ladingTestDrivePopup');
    }

    if (this.isRotationalCouponsNewDesign) {
      this.newSlickCoupons = { ...this.couponsslickoptions, ...this.newSlickCoupons };
    }
    this.shiftDigital = ShiftDigitalContainer.getShiftDigitalObject();

    if (this.shiftDigital) {
      try {
        this.shiftDigital.homePagePageView(true);
      } catch (e) {
        console.log(e);
      }
    }

    if (this.shiftDigital && typeof this.shiftDigital.homePageOverlay === 'function') {
      await isSatelliteLoaded();
      this.shiftDigital.homePagePageViewFord();
    }
    await fireTrackingPageViews(LANDING_PAGE_VIEW, 'home');
    if (window.hideFooterOnMobile || window.hideMobileCTAs) {
      this.loadMobileCompoments = true;
    }

    if (window.enablePopupVideo && window.videoURL) {
      let videoPopupSessionKey = VideoPopupSession.VideoPopupSessionExists('landingpage');
      if (!videoPopupSessionKey) {
        this.showVideoPopupModal = true;
        this.showOtherPopUpModals = true;
      }
    }

    const isDesktop = window.innerWidth > 800;

    let checkForQuickStart = CheckForQuickStartPopUp.checkForQuickStartCookie(isDesktop);
    if (!checkForQuickStart) {
      this.showOtherPopUpModals = true;

      if (this.showMitsubhishiPopUp) {
        const url = new URL(window.location.href);
        const popup = url.searchParams.get('popup');
        if (popup === 'false') {
          this.showMitsubhishiPopUp = false;
        }
      } else {
        this.showVideoPopup();
      }
      this.getBonusOfferShowAtEntry();
    }

    let couponInterval = '';
    couponInterval = setInterval(() => {
      const couponSpecials = this.$refs['coupons'];

      // if (couponSpecials) {
      //   clearInterval(couponInterval);
      //   couponSpecials.$on('afterChange', this.handleAfterChange);
      // }
    }, 200);

    triggerInventoryScrollEventOnHomePage();

    triggerSpecialsScrollEventOnHomePage();

    if (window.landingPopUpText && CheckForQuickStartPopUp.checkForLandingPopUp()) {
      this.showLandingPopUpModal = true;
    } else {
      this.showLandingPopUpModal = false;
    }

    setTimeout(() => {
      let tagManager = TagManagerContainer.getDealerObject();
      if (tagManager && typeof tagManager.getHomePage === 'function') {
        tagManager.getHomePage();
      }
    }, 0);

    this.showslick = true;
    if (this.$refs.checklandinghasiframe?.querySelector('iframe')) {
      document.getElementsByClassName('search-vehicle-component')[0].style.top = 0;
      document.getElementsByClassName('move-margin-top')[0].style.marginTop = 0;
    }

    this.onLoadCalls();

    this.checkForSlickEvents();
  },
};

const pinia = createPinia();
const app = createApp(landingPage);

app.use(pinia);
app.use(VueLazyLoad);
app.use(EqualHeightsPlugin);
app.use(LoadScript);
app.component('v-slick-carousel', VSlickCarousel);
app.mount('#main-container');
